import { useQuery } from "@apollo/client";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import {
  JobWorkflowStatus,
  JobsListDocument,
  JobsListPotentialCandidatesDocument,
  Location,
} from "@hire/schema";
import { stringToLocation } from "@hire/util/locations";
import { hireAppUser } from "@hire/util/user";

const WORKFLOW_STATUS_MAP: Record<string, JobWorkflowStatus[]> = {
  published: [JobWorkflowStatus.Completed, JobWorkflowStatus.Changed],
  review: [JobWorkflowStatus.QaInternal, JobWorkflowStatus.QaExternal],
  draft: [JobWorkflowStatus.RecruiterDraft],
  archived: [JobWorkflowStatus.Archived],
};

interface SearchParams {
  status?: string | undefined;
  locations?: Location[] | undefined;
}

export function useJobsList() {
  const [searchParams, setSearchParams] = useSearchParams({
    status: "published",
  });

  const updateSearchParams = useCallback(
    (newParams: SearchParams) => {
      setSearchParams(params => {
        return {
          ...Object.fromEntries(params.entries()),
          ...newParams,
        };
      });
    },
    [setSearchParams]
  );

  const status = searchParams.get("status") ?? "published";
  const workflowStatuses = WORKFLOW_STATUS_MAP[status];

  const searchParamsLocation = searchParams.get("locations");
  const location = searchParamsLocation
    ? stringToLocation(searchParamsLocation)
    : undefined;

  const locations: Location[] = location ? [location] : [];

  const variables = { workflowStatuses, locations };

  const { data, loading, error, refetch } = useQuery(JobsListDocument, {
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
    variables,
  });

  const { data: potentialCandidatesData } = useQuery(
    JobsListPotentialCandidatesDocument,
    {
      variables,
    }
  );

  const company = useMemo(
    () => hireAppUser(data?.me)?.currentCompany,
    [data?.me]
  );

  const jobs = useMemo(() => {
    const jobsWithPotentialCandidates = hireAppUser(potentialCandidatesData?.me)
      ?.currentCompany?.listJobs;
    if (!jobsWithPotentialCandidates) {
      return company?.listJobs;
    }
    return company?.listJobs.map(job => {
      const additionalData = jobsWithPotentialCandidates.find(
        ({ id }) => id === job.id
      );

      return {
        ...job,
        ...additionalData,
      };
    });
  }, [company, potentialCandidatesData]);

  return {
    updateSearchParams,
    loading,
    company,
    jobs,
    status,
    locations,
    location,
    error,
    refetch: () => refetch(variables),
  };
}
